// src/routes/userdashboardRoutes.js
import { lazy, Suspense, useEffect, useState, useCallback } from 'react';
import { Outlet } from 'react-router-dom';

// Guards & Layouts
import { AuthGuard, RoleBasedGuard } from 'src/auth/guard';
import DashboardLayout from 'src/layouts/dashboard';

// Components
import { LoadingScreen } from 'src/components/loading-screen';
import { useSnackbar } from 'src/components/snackbar';
import { fetcher } from 'src/utils/axios';

// Lazy-loaded pages
const IndexPage = lazy(() => import('src/pages/app-worker'));

// WORKER FETCH-LIST
const WorkerFetchListPage = lazy(() => import('src/pages/dashboard/fetchlistworker/list'));
const WorkerFetchListCreatePage = lazy(() => import('src/pages/dashboard/fetchlistworker/new'));
const WorkerFetchListEditPage = lazy(() => import('src/pages/dashboard/fetchlistworker/edit'));
const WorkerFetchListScanPage = lazy(() => import('src/pages/dashboard/fetchlistworker/scan'));

// JOB ORDER
const WorkJobListPage = lazy(() => import('src/pages/dashboard/workjobfetchlist/list'));
const WorkJobCreatePage = lazy(() => import('src/pages/dashboard/workjobfetchlist/new'));
const WorkJobEditPage = lazy(() => import('src/pages/dashboard/workjobfetchlist/edit'));
const WorkJobScanPage = lazy(() => import('src/pages/dashboard/workjobfetchlist/scan'));
const WorkFlowInventoryScanPage = lazy(() => import('src/pages/dashboard/workjobfetchlist/workflow-inventory'));

// SELECT PRODUCT
const SelectProductListPage = lazy(() => import('src/pages/dashboard/selectproduct/list'));
const WorkflowInitializeListPage = lazy(() => import('src/pages/dashboard/workflowinitialize/list'));
const SelectProductDetailsPage = lazy(() => import('src/sections/product-selector/view/product-selector-detail-view'));
const SelectProductCreatePage = lazy(() => import('src/pages/dashboard/selectproduct/new'));
const SelectProductEditPage = lazy(() => import('src/pages/dashboard/selectproduct/edit'));

// OTHER PAGES
const UserDiamondPage = lazy(() => import('src/pages/dashboard/user-diamond'));
const NodeWithoutMachineViewPage = lazy(() => import('src/pages/dashboard/nodewithoutmachine'));
const NodeWithoutMachineActionViewPage = lazy(() => import('src/pages/dashboard/node-without-machine-action'));

// ────────────────────────────────────────────────────────────────────────────────

export function WorkerDashboardRoutes() {
   const { enqueueSnackbar } = useSnackbar();
   const [allowedRoles, setAllowedRoles] = useState([]);

   const ROLES_API_URL = `${process.env.REACT_APP_HOST_API}/accounts-app/custom-roles/show/`;

   const fetchRoles = useCallback(async () => {
      try {
         const response = await fetcher([ROLES_API_URL])

         if (!response) throw new Error('Failed to fetch roles');

         const data = response;

         // Filter only active roles
         const roles = data
            .filter(role => role.is_active)
            .map(role => role.name);

         setAllowedRoles(roles.length > 0 ? roles : ['worker']); // fallback
      } catch (error) {
         console.error('Error loading roles for dashboard:', error);
         enqueueSnackbar('Failed to load permissions', { variant: 'error' });

         // Emergency fallback (better than blocking access completely)
         setAllowedRoles(['worker', 'Process Operator', 'Product Selector']);
      }
   }, [ROLES_API_URL, enqueueSnackbar]);

   useEffect(() => {
      fetchRoles();
   }, [fetchRoles]);

   // Show loader while roles are being fetched
   if (allowedRoles.length === 0) {
      return <LoadingScreen />;
   }

   return (
      <AuthGuard>
         <RoleBasedGuard
            hasContent
            roles={allowedRoles}           // ← fully dynamic from backend
            sx={{ py: 10 }}
         >
            <DashboardLayout>
               <Suspense fallback={<LoadingScreen />}>
                  <Outlet />
               </Suspense>
            </DashboardLayout>
         </RoleBasedGuard>
      </AuthGuard>
   );
}

// ────────────────────────────────────────────────────────────────────────────────
// Route configuration (export as array for react-router)
export const userdashboardRoutes = [
   {
      path: 'worker-dashboard',
      element: <WorkerDashboardRoutes />,   // ← our dynamic component
      children: [
         { element: <IndexPage />, index: true },

         // Worker Fetch List
         {
            path: 'workerfetchlist',
            children: [
               { element: <WorkerFetchListPage />, index: true },
               { path: 'list', element: <WorkerFetchListPage /> },
               { path: 'new', element: <WorkerFetchListCreatePage /> },
               { path: ':id/edit', element: <WorkerFetchListEditPage /> },
               { path: ':id/scan', element: <WorkerFetchListScanPage /> },
            ],
         },

         // Work Job / Job Order
         {
            path: 'workjob',
            children: [
               { element: <WorkJobListPage />, index: true },
               { path: 'list', element: <WorkJobListPage /> },
               { path: 'new', element: <WorkJobCreatePage /> },
               { path: ':id/edit', element: <WorkJobEditPage /> },
               { path: ':id/scan', element: <WorkJobScanPage /> },
               { path: ':id/workflow', element: <WorkFlowInventoryScanPage /> },
            ],
         },

         // Decision / Diamond
         {
            path: 'decision',
            children: [
               { element: <UserDiamondPage />, index: true },
            ],
         },

         // Node Without Machine
         {
            path: 'nodewithoutmachine',
            children: [
               { element: <NodeWithoutMachineViewPage />, index: true },
               { path: ':id/action', element: <NodeWithoutMachineActionViewPage /> },
            ],
         },

         // Select Product
         {
            path: 'selectproduct',
            children: [
               { element: <SelectProductListPage />, index: true },
               { path: 'list', element: <SelectProductListPage /> },
               { path: 'new', element: <SelectProductCreatePage /> },
               { path: 'details/:id/', element: <SelectProductDetailsPage /> },
               { path: ':id/edit', element: <SelectProductEditPage /> },
            ],
         },
         {
            path: 'workflowintialize',
            children: [
               { element: <WorkflowInitializeListPage />, index: true },
               { path: 'list', element: <WorkflowInitializeListPage /> },
            ],
         },
      ],
   },
];